// Editor controlado de "tarifas por código postal" (override de zona por CP).
// Comparten esta UX la configuración del operador (DriverPaymentSection) y el override
// por repartidor (DriverTariffs), igual que ComunaZonesEditor con las comunas.
//
// Shape de la API (modelo RELATIVO, sin `baseCost` absoluto legacy):
//   { postalCodes: ['M5500ABC', '5501'], adjustmentType: 'add' | 'subtract', amount }
//
// Jerarquía que aplica el motor: CPA exacto > CP de 4 dígitos > comuna/municipio > default.
// Solo se muestra en países con `postalCodeRates.enabled` (hoy Argentina); un operador
// chileno no ve esta sección y su payload queda idéntico al de antes de esta feature.
import React from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../../../../components/ui/Button";
import PostalCodeChipsInput, {
  validPostalCodes,
} from "../../../../components/postalCodeChipsInput";
import { useCountry } from "../../../../context/CountryContext";
import {
  DRIVER_VEHICLE_ORDER,
  DRIVER_VEHICLE_OPTIONS,
  driverVehicleLabel,
} from "../../../../utils/driverVehicleTypes";

const inputCls =
  "h-9 rounded-md border border-border bg-bg px-2 text-sm w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500";

let _uidSeq = 0;
const uid = () => `pc${++_uidSeq}`;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// API -> filas de UI. Una fila por regla (los códigos viven juntos en el chips input,
// que es justo la razón de ser de la regla: varios CP con el mismo ajuste).
export const postalZonesFromApi = (apiZones) =>
  (Array.isArray(apiZones) ? apiZones : [])
    .map((z) => ({
      id: uid(),
      postalCodes: Array.isArray(z.postalCodes) ? z.postalCodes : [],
      adjustmentType: z.adjustmentType === "subtract" ? "subtract" : "add",
      amount: z.amount != null ? String(z.amount) : "",
      vehicleTypes: Array.isArray(z.vehicleTypes) ? z.vehicleTypes : [],
    }))
    .filter((r) => r.postalCodes.length > 0);

// Filas de UI -> API. Mismo criterio de omisión que `zonesToApi`:
//   - se descartan las filas sin códigos VÁLIDOS o con monto <= 0 (ver
//     `countIncompletePostalRows`, que avisa antes de descartarlas);
//   - `vehicleTypes` se incluye solo si la fila tiene vehículos específicos.
// Los códigos se envían en su clave canónica (uppercase, sin separadores) para que el
// payload no cambie por cómo los escribió el operador.
export const postalZonesToApi = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((r) => {
      const z = {
        postalCodes: validPostalCodes(r.postalCodes),
        adjustmentType: r.adjustmentType === "subtract" ? "subtract" : "add",
        amount: Math.max(0, Math.round(num(r.amount))),
      };
      if (Array.isArray(r.vehicleTypes) && r.vehicleTypes.length > 0) {
        z.vehicleTypes = r.vehicleTypes;
      }
      return z;
    })
    .filter((z) => z.postalCodes.length > 0 && z.amount > 0);

/**
 * Filas que el guardado descartaría en silencio: sin ningún código válido, o con
 * monto <= 0. Alimenta el aviso previo al guardado (el descarte silencioso de una
 * tarifa es indistinguible, para el operador, de haberla guardado bien).
 */
export const countIncompletePostalRows = (rows) =>
  (Array.isArray(rows) ? rows : []).filter(
    (r) => validPostalCodes(r.postalCodes).length === 0 || Math.round(num(r.amount)) <= 0,
  ).length;

export default function PostalCodeZonesEditor({
  rows,
  onChange,
  currency = "CLP",
  baseCost,
  // Modo "diferenciado por vehículo": muestra el selector de vehículos por fila y
  // calcula el preview por vehículo (idéntico a ComunaZonesEditor).
  vehicleFilter = false,
  vehicleBaseCosts = null,
}) {
  const { t } = useTranslation();
  const { locale, labels } = useCountry();
  const communeLower = String(labels?.commune ?? "comuna").toLowerCase();

  const addRow = () =>
    onChange([
      ...(rows ?? []),
      { id: uid(), postalCodes: [], adjustmentType: "add", amount: "", vehicleTypes: [] },
    ]);
  const setRow = (i, patch) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

  const toggleVehicle = (i, v) =>
    setRow(i, (() => {
      const cur = Array.isArray(rows[i]?.vehicleTypes) ? rows[i].vehicleTypes : [];
      const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
      return { vehicleTypes: DRIVER_VEHICLE_ORDER.filter((x) => next.includes(x)) };
    })());
  const clearVehicles = (i) => setRow(i, { vehicleTypes: [] });

  const def = Math.round(num(baseCost));
  const fmt = (n) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency || "CLP",
      maximumFractionDigits: 0,
    }).format(n || 0);

  const resultingFare = (row) => {
    const amt = Math.round(num(row.amount));
    const signed = row.adjustmentType === "subtract" ? -amt : amt;
    return Math.max(0, def + signed);
  };

  const rowHasVehicles = (row) =>
    vehicleFilter && Array.isArray(row.vehicleTypes) && row.vehicleTypes.length > 0;
  const canPreviewByVehicle = (row) =>
    rowHasVehicles(row) && vehicleBaseCosts && typeof vehicleBaseCosts === "object";

  const resultingFareForVehicle = (row, v) => {
    const base = Math.round(num(vehicleBaseCosts?.[v]));
    const amt = Math.round(num(row.amount));
    const signed = row.adjustmentType === "subtract" ? -amt : amt;
    return Math.max(0, base + signed);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium text-fg">
          {t("Tarifas por código postal")}
        </label>
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus className="w-4 h-4" />}
          onClick={addRow}
          type="button"
        >
          {t("Agregar")}
        </Button>
      </div>
      <p className="text-xs text-fg-muted mb-2">
        {t("Un código postal exacto (CPA) tiene prioridad sobre el CP de 4 dígitos, y ambos sobre la tarifa por {{commune}}.", { commune: communeLower })}{" "}
        {t("Escribe cada código y confirma con Enter o coma.")}
      </p>

      {(!rows || rows.length === 0) && (
        <p className="text-xs text-fg-muted">
          {t("Sin códigos postales; la tarifa la define el {{commune}} de destino.", { commune: communeLower })}
        </p>
      )}

      {(rows ?? []).map((row, i) => {
        const amt = Math.round(num(row.amount));
        return (
          <div
            key={row.id ?? i}
            className="flex flex-wrap items-end gap-2 mb-2 p-2 rounded-md border border-border/60"
          >
            {/* Códigos postales de la regla */}
            <div className="flex flex-col gap-1 min-w-[220px] flex-1">
              <PostalCodeChipsInput
                label={t("Códigos postales")}
                value={row.postalCodes}
                onChange={(postalCodes) => setRow(i, { postalCodes })}
              />
            </div>

            {/* Sumar / Descontar */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-fg-muted">{t("Ajuste")}</span>
              <div className="inline-flex rounded-md border border-border overflow-hidden h-9">
                <button
                  type="button"
                  onClick={() => setRow(i, { adjustmentType: "add" })}
                  className={`px-3 text-sm ${
                    row.adjustmentType !== "subtract"
                      ? "bg-brand-500 text-white"
                      : "bg-bg text-fg-muted hover:text-fg"
                  }`}
                  title={t("Sumar a la tarifa base")}
                >
                  + {t("Sumar")}
                </button>
                <button
                  type="button"
                  onClick={() => setRow(i, { adjustmentType: "subtract" })}
                  className={`px-3 text-sm border-l border-border ${
                    row.adjustmentType === "subtract"
                      ? "bg-brand-500 text-white"
                      : "bg-bg text-fg-muted hover:text-fg"
                  }`}
                  title={t("Descontar de la tarifa base")}
                >
                  − {t("Descontar")}
                </button>
              </div>
            </div>

            {/* Monto */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-fg-muted">
                {t("Monto")} ({currency})
              </span>
              <input
                type="number"
                min="0"
                className={`${inputCls} max-w-[120px]`}
                value={row.amount}
                onChange={(e) => setRow(i, { amount: e.target.value })}
                placeholder={t("Ej: 500")}
                aria-label={t("Monto del ajuste por código postal")}
              />
            </div>

            {/* Tarifa resultante (preview) */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-fg-muted">{t("Tarifa resultante")}</span>
              <span className="h-9 inline-flex items-center text-sm font-medium text-fg whitespace-nowrap">
                {canPreviewByVehicle(row) ? (
                  row.vehicleTypes.map((v, idx) => (
                    <span key={v}>
                      {idx > 0 && <span className="text-fg-subtle"> · </span>}
                      <span className="text-fg-muted">{driverVehicleLabel(v, t)} </span>
                      {fmt(resultingFareForVehicle(row, v))}
                    </span>
                  ))
                ) : def > 0 ? (
                  fmt(resultingFare(row))
                ) : (
                  <span className="text-fg-muted">
                    {row.adjustmentType === "subtract" ? "−" : "+"}
                    {fmt(amt)} {t("s/ base")}
                  </span>
                )}
              </span>
            </div>

            <button
              type="button"
              className="h-9 px-2 text-fg-muted hover:text-status-error"
              onClick={() => removeRow(i)}
              aria-label={t("Quitar")}
            >
              <Trash2 className="w-4 h-4" />
            </button>

            {/* Filtro de vehículos (solo en modo diferenciado por vehículo). */}
            {vehicleFilter && (
              <div className="flex flex-col gap-1 w-full mt-1">
                <span className="text-xs text-fg-muted">{t("Vehículos")}</span>
                <div className="flex flex-wrap gap-1.5">
                  {(() => {
                    const selected = Array.isArray(row.vehicleTypes) ? row.vehicleTypes : [];
                    const allActive = selected.length === 0;
                    const chipCls = (active) =>
                      `h-7 px-2.5 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? "border-brand-500 bg-brand-50 text-brand-700"
                          : "border-border text-fg-muted hover:bg-bg-muted"
                      }`;
                    return (
                      <>
                        <button
                          type="button"
                          className={chipCls(allActive)}
                          onClick={() => clearVehicles(i)}
                          title={t("Aplica a todos los vehículos")}
                        >
                          {t("Todos")}
                        </button>
                        {DRIVER_VEHICLE_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            className={chipCls(selected.includes(opt.value))}
                            onClick={() => toggleVehicle(i, opt.value)}
                          >
                            {t(opt.labelKey)}
                          </button>
                        ))}
                      </>
                    );
                  })()}
                </div>
                {rowHasVehicles(row) && (
                  <span className="text-[11px] text-fg-subtle">
                    {t("La regla específica de vehículo le gana a la genérica del mismo código postal.")}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
